import React, { memo, useCallback, useRef, useState } from "react";
import { LoadingButton } from "@mui/lab";
import { Box, Stack, Typography } from "@mui/material";
import { CloudUpload, Description } from "@mui/icons-material";
import { FileEarmarkSpreadsheet } from "react-bootstrap-icons";
import { toast } from "react-toastify";
import TextFieldLabel from "app/components/common/TextFieldLabel";
import { ACCEPTED_UPLOAD_ACCEPT, ACCEPTED_UPLOAD_FORMATS } from "../constants";
import { isAcceptedUploadFile } from "../bulkAdmissionUtils";

const spreadsheetIconStyle = {
  border: "1px solid #3D3989",
  color: "#3D3989",
  backgroundColor: "#f0f0f0",
  borderRadius: "10px",
  padding: "10px",
};

const BulkFileUploadArea = ({
  title = "Download the sample file needed for bulk upload",
  buttonLabel = "Download Sample File",
  sampleLoading = false,
  uploadLoading = false,
  onDownload,
  onFileSelect,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);

  const handleValidFile = useCallback(
    (file) => {
      if (!file) return;

      if (!isAcceptedUploadFile(file)) {
        toast.error("Please upload .xlsx, .xls or .csv file only.");
        return;
      }

      onFileSelect(file);
    },
    [onFileSelect]
  );

  const handleDragEnter = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current += 1;
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current -= 1;

    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragging(false);
      handleValidFile(event.dataTransfer.files?.[0]);
    },
    [handleValidFile]
  );

  const handleInputChange = useCallback(
    (event) => {
      handleValidFile(event.target.files?.[0]);
      event.target.value = null;
    },
    [handleValidFile]
  );

  return (
    <Stack gap={3} alignItems="center" justifyContent="center" sx={{ py: 2 }}>
      <FileEarmarkSpreadsheet size={80} style={spreadsheetIconStyle} />
      <TextFieldLabel title={title} style={{ fontSize: "1.25rem" }} />

      <LoadingButton
        variant="outlined"
        color="secondary"
        size="large"
        loading={sampleLoading}
        onClick={onDownload}
      >
        {buttonLabel}
      </LoadingButton>

      <Box
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          width: "100%",
          maxWidth: 520,
          border: "2px dashed",
          borderColor: isDragging ? "#3D3989" : "#B6C8D6",
          backgroundColor: isDragging ? "#F6F8FC" : "#FAFBFC",
          borderRadius: "12px",
          p: 4,
          textAlign: "center",
          transition: "all 0.2s ease",
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_UPLOAD_ACCEPT}
          onChange={handleInputChange}
          style={{ display: "none" }}
        />

        <Stack alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: isDragging ? "#E8E6F5" : "#EEF2F6",
            }}
          >
            {isDragging ? (
              <CloudUpload sx={{ color: "#3D3989", fontSize: 28 }} />
            ) : (
              <Description sx={{ color: "#6B7280", fontSize: 28 }} />
            )}
          </Box>

          <Typography sx={{ color: "#111827", fontSize: "15px" }}>
            Drag & drop your file here
          </Typography>

          <Typography sx={{ color: "#6B7280", fontSize: "13px" }}>
            Supports {ACCEPTED_UPLOAD_FORMATS.join(", ")}
          </Typography>

          <LoadingButton
            loading={uploadLoading}
            variant="contained"
            color="secondary"
            size="large"
            onClick={() => fileInputRef.current?.click()}
          >
            Choose File
          </LoadingButton>
        </Stack>
      </Box>
    </Stack>
  );
};

export default memo(BulkFileUploadArea);
